// Luxe · First-Run Onboarding Modal (Prompt 4D)
// Rendered by AuthContext when the authenticated user carries
// `onboarding_status === "not_started" | "in_progress"`. Wraps the
// business profile + pricing defaults setup + Starter Materials
// introduction + Sample Project creation. Every step except step 1
// (Welcome) can be skipped. All writes hit the same canonical
// /profile and /settings endpoints — no onboarding-only shadow storage.
import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { X, ArrowRight, ArrowLeft, Check, Sparkles } from "lucide-react";

const STEPS = ["welcome", "business", "pricing", "starter", "sample", "done"];

export default function OnboardingModal({ user, onFinish }) {
  const [step, setStep] = useState(user?.onboarding_step || "welcome");
  const [profile, setProfile] = useState({ company_name: "", email: user?.email || "", phone: "", address: "", region: "AU", currency: "AUD" });
  const [pricing, setPricing] = useState({ labour_rate: 120, margin_rate: 30, tax_rate: 10 });
  const [busy, setBusy] = useState(false);
  const [creatingSample, setCreatingSample] = useState(false);

  useEffect(() => {
    api.get("/profile").then((r) => {
      setProfile((prev) => ({ ...prev, ...r.data }));
    }).catch(() => {});
    api.get("/settings/pricing").then((r) => {
      if (r.data) setPricing((prev) => ({ ...prev, ...r.data }));
    }).catch(() => {});
  }, []);

  const persist = async (nextStep, statusUpdate) => {
    try {
      await api.patch("/onboarding", {
        step: nextStep,
        ...(statusUpdate ? { status: statusUpdate } : {}),
      });
    } catch (_) {}
  };

  const advance = (next) => { setStep(next); persist(next, "in_progress"); };

  const finishOnboarding = async (opts = {}) => {
    setBusy(true);
    try {
      await api.patch("/onboarding", { status: opts.skipped ? "skipped" : "completed", step: "done" });
      onFinish && onFinish();
    } finally { setBusy(false); }
  };

  const saveBusiness = async () => {
    setBusy(true);
    try {
      await api.put("/profile", profile);
      advance("pricing");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const savePricing = async () => {
    setBusy(true);
    try {
      await api.put("/settings/pricing", pricing);
    } catch (_) { /* pricing endpoint optional in some builds */ }
    advance("starter");
    setBusy(false);
  };

  const createSample = async () => {
    setCreatingSample(true);
    try {
      await api.post("/onboarding/sample");
      toast.success("Sample project ready");
      advance("done");
    } catch (e) {
      // 409 = user previously deleted it — still fine, just advance.
      if (e?.response?.status === 409) advance("done");
      else toast.error(formatErr(e));
    } finally { setCreatingSample(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4 overflow-y-auto"
         data-testid="onboarding-modal">
      <div className="bg-card border border-border max-w-2xl w-full p-10 my-8 relative"
           data-testid={`onboarding-step-${step}`}>
        <button data-testid="btn-onboarding-skip-all"
                onClick={() => finishOnboarding({ skipped: true })}
                className="absolute top-4 right-4 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">
          Skip for now
        </button>

        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-8">
          {STEPS.slice(0, STEPS.length - 1).map((s, i) => (
            <div key={s} className={`h-0.5 flex-1 ${STEPS.indexOf(step) >= i ? "bg-foreground" : "bg-border"}`}/>
          ))}
        </div>

        {step === "welcome" && (
          <div data-testid="onboarding-welcome">
            <div className="overline">Welcome</div>
            <h2 className="font-serif font-light mt-2 mb-3" style={{ fontSize: "38px", lineHeight: 1.1 }}>Welcome to Subtend Studio</h2>
            <p className="text-muted-foreground max-w-lg">Design, visualise, price and present premium media-wall projects in one workflow. This quick setup takes about 90 seconds.</p>
            <button data-testid="btn-onboarding-start"
                    onClick={() => advance("business")}
                    className="btn-primary mt-8">
              <Sparkles className="w-4 h-4" strokeWidth={1.5}/> Get started
            </button>
          </div>
        )}

        {step === "business" && (
          <div data-testid="onboarding-business">
            <div className="overline">Step 1 · Business</div>
            <h2 className="font-serif font-light mt-2 mb-3" style={{ fontSize: "32px", lineHeight: 1.1 }}>Your studio details</h2>
            <p className="text-sm text-muted-foreground mb-6">Appears on every proposal and PDF.</p>
            <div className="space-y-4">
              <div><label className="overline block mb-1">Studio / company name</label>
                <input data-testid="onb-input-company" className="luxe-input" value={profile.company_name || ""}
                       onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}/></div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="overline block mb-1">Contact email</label>
                  <input data-testid="onb-input-email" className="luxe-input" value={profile.email || ""}
                         onChange={(e) => setProfile({ ...profile, email: e.target.value })}/></div>
                <div><label className="overline block mb-1">Phone</label>
                  <input data-testid="onb-input-phone" className="luxe-input" value={profile.phone || ""}
                         onChange={(e) => setProfile({ ...profile, phone: e.target.value })}/></div>
              </div>
              <div><label className="overline block mb-1">Address</label>
                <input data-testid="onb-input-address" className="luxe-input" value={profile.address || ""}
                       onChange={(e) => setProfile({ ...profile, address: e.target.value })}/></div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="overline block mb-1">Country / region</label>
                  <select data-testid="onb-select-region" className="luxe-input"
                          value={profile.region || "AU"}
                          onChange={(e) => setProfile({ ...profile, region: e.target.value })}>
                    <option value="AU">Australia</option>
                    <option value="NZ">New Zealand</option>
                    <option value="UK">United Kingdom</option>
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                  </select></div>
                <div><label className="overline block mb-1">Currency</label>
                  <select data-testid="onb-select-currency" className="luxe-input"
                          value={profile.currency || "AUD"}
                          onChange={(e) => setProfile({ ...profile, currency: e.target.value })}>
                    <option value="AUD">AUD</option><option value="NZD">NZD</option>
                    <option value="GBP">GBP</option><option value="USD">USD</option>
                    <option value="CAD">CAD</option>
                  </select></div>
              </div>
              <div className="text-[10px] text-muted-foreground italic">Full multi-currency / imperial support is arriving in a later release — AUD stays fully supported today.</div>
            </div>
            <div className="flex justify-between mt-8">
              <button data-testid="btn-onboarding-back" onClick={() => setStep("welcome")} className="btn-outline">
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5}/> Back
              </button>
              <button data-testid="btn-onboarding-business-continue" onClick={saveBusiness} disabled={busy} className="btn-primary">
                Continue <ArrowRight className="w-4 h-4" strokeWidth={1.5}/>
              </button>
            </div>
          </div>
        )}

        {step === "pricing" && (
          <div data-testid="onboarding-pricing">
            <div className="overline">Step 2 · Pricing basics</div>
            <h2 className="font-serif font-light mt-2 mb-3" style={{ fontSize: "32px", lineHeight: 1.1 }}>Your default pricing</h2>
            <p className="text-sm text-muted-foreground mb-6">You can change these anytime in Settings.</p>
            <div className="grid md:grid-cols-3 gap-4">
              <div><label className="overline block mb-1">Labour rate</label>
                <input data-testid="onb-input-labour" type="number" step="1" className="luxe-input"
                       value={pricing.labour_rate}
                       onChange={(e) => setPricing({ ...pricing, labour_rate: parseFloat(e.target.value || 0) })}/>
                <div className="text-[10px] text-muted-foreground mt-1">per hour</div></div>
              <div><label className="overline block mb-1">Margin %</label>
                <input data-testid="onb-input-margin" type="number" step="1" className="luxe-input"
                       value={pricing.margin_rate}
                       onChange={(e) => setPricing({ ...pricing, margin_rate: parseFloat(e.target.value || 0) })}/>
                <div className="text-[10px] text-muted-foreground mt-1">default markup</div></div>
              <div><label className="overline block mb-1">Tax %</label>
                <input data-testid="onb-input-tax" type="number" step="0.5" className="luxe-input"
                       value={pricing.tax_rate}
                       onChange={(e) => setPricing({ ...pricing, tax_rate: parseFloat(e.target.value || 0) })}/>
                <div className="text-[10px] text-muted-foreground mt-1">e.g. GST</div></div>
            </div>
            <div className="flex justify-between mt-8">
              <button data-testid="btn-onboarding-back" onClick={() => setStep("business")} className="btn-outline">
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5}/> Back
              </button>
              <div className="flex gap-2">
                <button data-testid="btn-onboarding-pricing-skip" onClick={() => advance("starter")} className="btn-outline">
                  Use defaults
                </button>
                <button data-testid="btn-onboarding-pricing-continue" onClick={savePricing} disabled={busy} className="btn-primary">
                  Continue <ArrowRight className="w-4 h-4" strokeWidth={1.5}/>
                </button>
              </div>
            </div>
          </div>
        )}

        {step === "starter" && (
          <div data-testid="onboarding-starter">
            <div className="overline">Step 3 · Starter materials</div>
            <h2 className="font-serif font-light mt-2 mb-3" style={{ fontSize: "32px", lineHeight: 1.1 }}>A curated palette to start with</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Subtend Studio ships with 32 hand-picked timbers, stones, cabinetry finishes and specialty materials so you can begin designing immediately.
              When you use one, it's copied into your library where you can price and customise it freely.
            </p>
            <div className="grid grid-cols-4 gap-2 mb-8">
              {["Timber", "Stone", "Concrete", "Acoustic", "Cabinetry", "Panels", "Paint", "Specialty"].map((c) => (
                <div key={c} className="border border-border p-3 text-center text-xs">{c}</div>
              ))}
            </div>
            <div className="flex justify-between">
              <button data-testid="btn-onboarding-back" onClick={() => setStep("pricing")} className="btn-outline">
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5}/> Back
              </button>
              <button data-testid="btn-onboarding-starter-continue" onClick={() => advance("sample")} className="btn-primary">
                Continue <ArrowRight className="w-4 h-4" strokeWidth={1.5}/>
              </button>
            </div>
          </div>
        )}

        {step === "sample" && (
          <div data-testid="onboarding-sample">
            <div className="overline">Step 4 · Sample project</div>
            <h2 className="font-serif font-light mt-2 mb-3" style={{ fontSize: "32px", lineHeight: 1.1 }}>See a finished project</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Add a clearly-labelled Sample Project so you can explore the full workflow — Brief · Design · Visualise · Price · Proposal · Approval — with real content. You can delete it at any time.
            </p>
            <div className="flex justify-between">
              <button data-testid="btn-onboarding-back" onClick={() => setStep("starter")} className="btn-outline">
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5}/> Back
              </button>
              <div className="flex gap-2">
                <button data-testid="btn-onboarding-sample-skip" onClick={() => advance("done")} className="btn-outline">
                  Skip
                </button>
                <button data-testid="btn-onboarding-sample-create" onClick={createSample} disabled={creatingSample} className="btn-primary">
                  {creatingSample ? "Creating…" : "Create sample project"} <ArrowRight className="w-4 h-4" strokeWidth={1.5}/>
                </button>
              </div>
            </div>
          </div>
        )}

        {step === "done" && (
          <div data-testid="onboarding-done">
            <div className="overline" style={{ color: "#A68A64" }}>You're ready</div>
            <h2 className="font-serif font-light mt-2 mb-4" style={{ fontSize: "38px", lineHeight: 1.05 }}>Welcome aboard.</h2>
            <p className="text-muted-foreground mb-8">Two ways to get going — most Subtend Studio studios jump straight to their first real project.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <button data-testid="btn-onboarding-create-project"
                      onClick={async () => { await finishOnboarding(); window.location.href = "/projects/new"; }}
                      className="luxe-card p-6 text-left hover:bg-muted/40 transition-colors">
                <div className="overline text-[10px]" style={{ color: "#A68A64" }}>Recommended</div>
                <div className="font-serif text-xl mt-2">Create my first project</div>
                <div className="text-xs text-muted-foreground mt-2">Start a real project from scratch and design a media wall for a client.</div>
              </button>
              <button data-testid="btn-onboarding-explore-sample"
                      onClick={async () => { await finishOnboarding(); window.location.href = user?.sample_project_id ? `/projects/${user.sample_project_id}` : "/projects"; }}
                      className="luxe-card p-6 text-left hover:bg-muted/40 transition-colors">
                <div className="overline text-[10px]">Optional</div>
                <div className="font-serif text-xl mt-2">Explore the sample</div>
                <div className="text-xs text-muted-foreground mt-2">See a finished project and browse how it moves through every stage.</div>
              </button>
            </div>
            <div className="flex justify-end mt-6">
              <button data-testid="btn-onboarding-finish" onClick={() => finishOnboarding()} disabled={busy} className="btn-outline">
                <Check className="w-4 h-4" strokeWidth={1.5}/> Just take me to the dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
